import { useMemo } from 'react';

import { parseResourceNames } from '@spotify/confidence-core-react';
import type { ChatEventFragment } from '@spotify/confidence-graphql';
import {
  createCodeBlockExtension,
  useChatContext,
} from '@spotify/plugin-confidence-chat-react';

import { recordingsPlugin } from '../plugin';
import { RecordingsTable } from './RecordingsTable';
import { parseSearchResult } from './searchResult';
import type { RecordingHit } from './types';

const RECORDING_CODE_BLOCK_LANGUAGES = ['recording', 'recordings'];

const collectSearchedRecordings = (
  events: readonly ChatEventFragment[],
): ReadonlyMap<string, RecordingHit> => {
  const hits = new Map<string, RecordingHit>();
  for (const event of events) {
    const result = parseSearchResult(event.toolResult?.content);
    for (const hit of result?.recordings ?? []) {
      // Later searches overwrite earlier hits for the same recording.
      hits.set(hit.name, hit);
    }
  }
  return hits;
};

export const RecordingsCodeBlock = ({ names }: { names: string[] }) => {
  const chatEvents = useChatContext()?.chatEvents;
  const searchedRecordings = useMemo(
    () => collectSearchedRecordings(chatEvents ?? []),
    [chatEvents],
  );
  const recordings = useMemo(
    () =>
      names.map(
        (name): RecordingHit =>
          searchedRecordings.get(name) ?? { name, display_name: '' },
      ),
    [names, searchedRecordings],
  );

  return <RecordingsTable recordings={recordings} />;
};

export const RecordingsCodeBlockExtension = recordingsPlugin.provide(
  createCodeBlockExtension({
    id: 'recordings-code-block',
    predicate: ({ language }) =>
      RECORDING_CODE_BLOCK_LANGUAGES.includes(language),
    render: ({ content }) => {
      const names = parseResourceNames(content, 'recordings');
      return names.length > 0 ? <RecordingsCodeBlock names={names} /> : null;
    },
  }),
);
